import { useEffect, useState, useMemo } from 'react';
import { ProSidebar, Menu, MenuItem, SidebarHeader } from 'react-pro-sidebar';
import 'react-pro-sidebar/dist/css/styles.css';
import { Box, Typography, Badge, Tooltip, useMediaQuery } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { gas_sidebar_options, electricity_sidebar_options } from '@data';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import { COMMON_PATH, ELECTRICITY_PATH, GAS_PATH } from '@utils';
import { useGetChatUnreadMessageCountQuery } from '@store/feature/chat/chatApi';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import folderIcon from '@assets/svg/folder.svg';

const Item = ({ title, to, icon, suffix, tooltipText, isCollapsed }) => {
    const location = useLocation();

    // Only highlight if the current path matches exactly or starts with to + '/' (for parent links)
    const isActive =
        location.pathname === to ||
        (to !== '/' && location.pathname.startsWith(to + '/'));

    const menuItem = (
        <MenuItem active={isActive} icon={icon} suffix={suffix}>
            {!isCollapsed && <Typography variant="body2">{title}</Typography>}
            <Link to={to} />
        </MenuItem>
    );

    return isCollapsed ? (
        <Tooltip title={tooltipText || title} arrow placement="right">
            {menuItem}
        </Tooltip>
    ) : (
        menuItem
    );
};

const Sidebar = () => {
    const { user } = useSelector((state) => state.user);
    const { data: unreadMessage } = useGetChatUnreadMessageCountQuery();
    const location = useLocation();

    const isSmallScreen = useMediaQuery('(max-width: 900px)');
    const [isCollapsed, setIsCollapsed] = useState(isSmallScreen);

    const gasSidebar = useMemo(() => {
        // If the user object is not loaded or is empty, return an empty array to avoid flashing the full sidebar
        if (!user || Object.keys(user).length === 0) return [];
        const tempGasSidebar = cloneDeep(gas_sidebar_options);

        // Route market entries to an available currency tab when EUR is hidden.
        const theIndex = tempGasSidebar[0].childrens.findIndex(
            (item) => item.label === 'THE'
        );
        if (theIndex !== -1) {
            tempGasSidebar[0].childrens[theIndex].link =
                user?.HideNCG === true && user?.HideNCGCHF === false
                    ? GAS_PATH.THE_CHF
                    : GAS_PATH.THE_EUR;
        }

        const ttfIndex = tempGasSidebar[0].childrens.findIndex(
            (item) => item.label === 'TTF'
        );
        if (ttfIndex !== -1) {
            tempGasSidebar[0].childrens[ttfIndex].link =
                user?.HideTTF === true && user?.HideTTFCHF === false
                    ? GAS_PATH.TTF_CHF
                    : GAS_PATH.TTF_EUR;
        }

        const pegIndex = tempGasSidebar[0].childrens.findIndex(
            (item) => item.label === 'PEG'
        );
        if (pegIndex !== -1) {
            tempGasSidebar[0].childrens[pegIndex].link =
                user?.HidePEGNord === true && user?.HidePEGNordCHF === false
                    ? GAS_PATH.PEG_CHF
                    : GAS_PATH.PEG_EUR;
        }

        // Hide THE when both general and CHF variant are unchecked (hidden)
        if (user?.HideNCG && user?.HideNCGCHF)
            tempGasSidebar[0].childrens = tempGasSidebar[0].childrens.filter(
                (item) => item.label !== 'THE'
            );
        // Hide TTF when both general and TTF variant are unchecked (hidden)
        if (user?.HideTTF && user?.HideTTFCHF)
            tempGasSidebar[0].childrens = tempGasSidebar[0].childrens.filter(
                (item) => item.label !== 'TTF'
            );
        // Hide PEG when both general and PEG variant are unchecked (hidden)
        if (user?.HidePEGNord && user?.HidePEGNordCHF)
            tempGasSidebar[0].childrens = tempGasSidebar[0].childrens.filter(
                (item) => item.label !== 'PEG'
            );
        if (user?.HideEEX)
            tempGasSidebar[0].childrens = tempGasSidebar[0].childrens.filter(
                (item) => item.label !== 'EEX'
            );

        if (user?.HideIntraday)
            tempGasSidebar[0].childrens = tempGasSidebar[0].childrens.filter(
                (item) => item.label !== 'Intraday'
            );
        if (user?.HideOrders)
            tempGasSidebar[1].childrens = tempGasSidebar[1].childrens.filter(
                (item) => item.label !== 'Orders'
            );

        if (user?.HidePortfolio) {
            // remove portfolio object from tempGasSidebar not children
            const index = tempGasSidebar.findIndex(
                (item) => item.header === 'Portfolio'
            );
            if (index !== -1) {
                tempGasSidebar.splice(index, 1);
            }
        }

        return tempGasSidebar;
    }, [user]);

    useEffect(() => {
        setIsCollapsed(isSmallScreen);
    }, [isSmallScreen]);

    const hostname = window.location.hostname;

    const getColor = () => {
        if (hostname.includes('staging.priceblick.com')) {
            return 'bg-red-500';
        } else return 'bg-[#27485D]'; // Default color for other environments
    };

    return (
        <ProSidebar collapsed={isCollapsed}>
            <SidebarHeader
                // className="bg-[#27485D] h-[69px]"
                className={`${getColor()} h-[69px]`}
                style={{ position: 'sticky', top: 0, zIndex: 2 }}
            >
                <Menu>
                    <MenuItem>
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            {!isCollapsed ? (
                                <Link
                                    to={COMMON_PATH.HOME}
                                    style={{
                                        borderRight: '3px solid #fff',
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        textDecoration: 'none',
                                        paddingRight: '10px'
                                    }}
                                >
                                    Price
                                    <span style={{ color: '#2EA3F0' }}>
                                        Blick
                                    </span>
                                </Link>
                            ) : (
                                <Link
                                    to={COMMON_PATH.HOME}
                                    style={{
                                        borderRight: '3px solid #fff',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        textDecoration: 'none',
                                        flexDirection: 'column',
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingRight: '10px'
                                    }}
                                >
                                    <span> Price </span>
                                    <span style={{ color: '#2EA3F0' }}>
                                        Blick
                                    </span>
                                </Link>
                            )}
                        </Box>
                    </MenuItem>
                </Menu>
            </SidebarHeader>
            <Menu style={{ overflowY: 'auto', height: 'calc(100vh - 69px)' }}>
                {(location.pathname.includes('gas')
                    ? gasSidebar
                    : electricity_sidebar_options
                )?.map((options, index) => (
                    <Box key={index}>
                        {!isCollapsed && (
                            <Typography
                                variant="h6"
                                sx={{ paddingLeft: '16px' }}
                            >
                                {options.header}
                            </Typography>
                        )}
                        {options.childrens?.map((item, childIndex) => {
                            if (item.isAdmin && !user?.Admin) return null;
                            return (
                                <Item
                                    key={`${index}-${childIndex}`}
                                    title={item.label}
                                    to={item.link}
                                    icon={item.icon}
                                    tooltipText={item.label}
                                    isCollapsed={isCollapsed}
                                />
                            );
                        })}
                    </Box>
                ))}
                <MenuItem
                    icon={<ChatOutlinedIcon />}
                    suffix={
                        unreadMessage?.count &&
                        unreadMessage?.count >= 1 && (
                            <Badge
                                badgeContent={
                                    unreadMessage?.count > 9
                                        ? '9+'
                                        : unreadMessage?.count
                                }
                                color="primary"
                            />
                        )
                    }
                    style={{
                        paddingRight: '16px',
                        backgroundColor:
                            location.pathname.includes(GAS_PATH.CHAT) ||
                            location.pathname.includes(ELECTRICITY_PATH.CHAT)
                                ? '#DFF2FF'
                                : 'inherit',
                        color:
                            location.pathname.includes(GAS_PATH.CHAT) ||
                            location.pathname.includes(ELECTRICITY_PATH.CHAT)
                                ? '#3AACFE'
                                : 'inherit',
                        width:
                            location.pathname.includes(GAS_PATH.CHAT) ||
                            location.pathname.includes(ELECTRICITY_PATH.CHAT)
                                ? '90%'
                                : 'inherit',
                        borderRadius:
                            location.pathname.includes(GAS_PATH.CHAT) ||
                            location.pathname.includes(ELECTRICITY_PATH.CHAT)
                                ? '5px'
                                : 'inherit'
                    }}
                >
                    {!isCollapsed && <Typography>Chat</Typography>}
                    <Link
                        to={
                            location.pathname.includes('gas')
                                ? GAS_PATH.CHAT
                                : ELECTRICITY_PATH.CHAT
                        }
                    />
                </MenuItem>
            </Menu>
        </ProSidebar>
    );
};

export default Sidebar;
